import React, {
  useCallback, useMemo, useState, ReactElement,
} from 'react';
import Card from '../../../components/card/card.component';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import Button from '../../../components/button/button.component';
import ModalButtonBlock from '../../../components/modal-button-block/modal-button-block.component';
import LeaveConfirmModal from '../../../components/leave-confirm-modal/leave-confirm-modal.component';
import Tooltip from '../../../components/tooltip/tooltip.component';
import ModalLayout from '../../../components/modal-layout/modal-layout.component';
import RequiredMark from '../../../components/required-mark/required-mark.component';
import Loader from '../../../components/loader/loader.component';
import ActionButton from '../../../components/action-button/action-button.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import Hint from '../../../components/hint/hint.component';
import Form from '../../../components/form/form.component';
import FormFieldSelect from '../../../components/form-field-select/form-field-select.component';
import FormFieldMultiSelect from '../../../components/form-field-multi-select/form-field-multi-select.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import FormFieldNumber from '../../../components/form-field-number/form-field-number.component';
import FormFieldCheckbox from '../../../components/form-field-checkbox/form-field-checkbox.component';
import FormFieldDate from '../../../components/form-field-date/form-field-date.component';
import FormFieldAddress from '../../../components/form-field-address/form-field-address.component';
import FormFieldUpload from '../../../components/form-field-upload/form-field-upload.component';
import FieldAddressBlock from '../../../components/field-address-block/field-address-block.component';
import FieldSeparatedBlock from '../../../components/field-separated-block/field-separated-block.component';
import MenuItemAddNewItem from '../../../components/menu-item-add-new-item/menu-item-add-new-item.component';
import ImageFileContainer from '../../../components/image-file-container/image-file-container.component';
import TableRowAddNewItem from '../../../components/table-row-add-new-item/table-row-add-new-item.component';
import MenuItemList from '../../../components/menu-item-list/menu-item-list.component';
import RemoveIcon from '../../../icons/remove-icon';
import CancelFilledIcon from '../../../icons/cancel-filled-icon';
import StructureService from '../../../services/structure-service';
import FormService from '../../../services/form-service';
import useScreenSmall from '../../../hooks/use-screen-small';
import { FILE_EXTENSION_LIST_IMAGE } from '../../../constants/system/file-extensions';
import { CONTACT_CREATE } from '../../../constants/entity/permissions';
import CompanyDefaultImage from '../../../static/images/company-default-image.png';

const AssetModal: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const COLUMNS_TOTAL: number = 2;

  const {
    isOpen,
    isSaving,
    isSaveDisabled,
    isEditMode,
    isNewSellerVisible,
    isMultiplePortfolioGroupView,
    assetPhotoFile,
    assetFieldNewItems,
    portfolioList,
    assetStatusList,
    assetTypeList,
    measureUnitList,
    pricingTypeList,
    assetOwnershipStructureTypeList,
    contactCompanyList,
    tagList,
    saveFormDeps,
    onCreate,
    onCancel,
    onCloseModal,
    onTagsChange,
    onAssetTypesChange,
    onAssetSellersChange,
    onSubmitCreateForm,
    onAddressSearchCompleted,
    onAddressFieldKeyDown,
    onFieldDeleteClick,
    onAddNewFieldItem,
    onOpenNewSellerForm,
    onCloseNewSellerForm,
    onContactCompanySearch,
    onContactCompanySearchClose,
    onTagSearch,
    onTagSearchClose,
    onAssetPhotoChange,
    onAssetPhotoDelete,
  } = props;

  const isSmallScreen: boolean = useScreenSmall();

  const [selectedSellers, setSelectedSellers]: [Contact | null, Function] = useState(null);
  const [selectedAssetTypes, setSelectedAssetTypes]: [AssetType | null, Function] = useState(null);
  const [selectedTags, setSelectedTags]: [Tag | null, Function] = useState(null);
  const [isLeaveConfirmModalOpen, setIsLeaveConfirmModalOpen]: [boolean, Function] = useState(false);

  const assetId: number = FormService.FORM_KEY_NEW;
  const portfolioId: number = FormService.FORM_KEY_NEW;
  const formKeyAsset: string = FormService.getIndexedFormFieldKey(assetId);
  const formKeyPortfolio: string = FormService.getIndexedFormFieldKey(portfolioId);
  const formPrefix: string = `assetListData[${formKeyPortfolio}].assets[${formKeyAsset}]`;

  const menuItemPortfolioList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    isGrouped: isMultiplePortfolioGroupView,
    groupKey: 'group',
    groupItemLabelKey: 'name',
    optionItemLabelKey: 'name',
    optionList: portfolioList,
  }), [portfolioList, isMultiplePortfolioGroupView]);

  const menuItemAssetStatusList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: assetStatusList,
  }), [assetStatusList]);

  const menuItemMeasureUnitList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: measureUnitList,
  }), [measureUnitList]);

  const menuItemPricingTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: pricingTypeList,
  }), [pricingTypeList]);

  const menuItemAssetOwnershipStructureTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: assetOwnershipStructureTypeList,
  }), [assetOwnershipStructureTypeList]);

  const menuItemContactCompanySellerList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: contactCompanyList,
    defaultImage: CompanyDefaultImage,
    selectedItemList: selectedSellers,
  }), [contactCompanyList, selectedSellers]);

  const menuItemAssetTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: assetTypeList,
    selectedItemList: selectedAssetTypes,
  }), [assetTypeList, selectedAssetTypes]);

  const menuItemTagList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionItemLabelKey: 'name',
    optionList: tagList,
    selectedItemList: selectedTags,
  }), [tagList, selectedTags]);

  const addNewItem: MouseEventHandlerFunction = useCallback((): void => {
    if (onAddNewFieldItem) {
      onAddNewFieldItem({
        id: FormService.getIndexedFormFieldKey(StructureService.getNewItemId(assetFieldNewItems || [], [])),
        name: '',
        value: '',
      });
    }
  }, [assetFieldNewItems]);

  const deleteClick: Function = useCallback((assetFieldId: number): void => {
    if (onFieldDeleteClick) {
      onFieldDeleteClick(portfolioId, assetId, assetFieldId);
    }
  }, [portfolioId, assetId]);

  const onAssetPhotoFileChange: Function = useCallback((file: File): void => {
    if (onAssetPhotoChange) {
      onAssetPhotoChange(portfolioId, assetId, file);
    }
  }, [portfolioId, assetId]);

  const onAssetPhotoFileDelete: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    if (onAssetPhotoDelete) {
      onAssetPhotoDelete(portfolioId, assetId);
    }

    event.stopPropagation();
  }, [portfolioId, assetId]);

  const applyChangesAndCloseModal: MouseEventHandlerFunction = useCallback((): void => {
    if (onCreate) {
      if (onSubmitCreateForm) {
        onSubmitCreateForm((): void => {
          onCreate();
        });
      }
    } else if (onCloseModal) {
      onCloseModal();
    }
  }, [isSaveDisabled, isEditMode, ...(saveFormDeps || [])]);

  const cancelChangesAndCloseModal: MouseEventHandlerFunction = useCallback((): void => {
    if (onCancel && !isEditMode) {
      onCancel();
    }

    if (onCloseModal) {
      onCloseModal();
    }
  }, [isEditMode]);

  const closeModalByClickAway: Function = useCallback((): void => {
    if (!isSaveDisabled && !isEditMode) {
      setIsLeaveConfirmModalOpen(true);
    } else if (onCloseModal) {
      onCloseModal();
    }
  }, [isSaveDisabled, isEditMode]);

  const closeLeaveConfirmModal: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);
  }, []);

  const onLeaveConfirm: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);

    if (onCancel) {
      onCancel();
    }

    if (onCloseModal) {
      onCloseModal();
    }
  }, []);

  const sellersChange: Function = useCallback((contactValues: Contact): void => {
    if (onAssetSellersChange) {
      onAssetSellersChange(portfolioId, assetId, contactValues);
    }

    setSelectedSellers(contactValues);
  }, [portfolioId, assetId]);

  const assetTypesChange: Function = useCallback((assetTypeValues: AssetType[]): void => {
    if (onAssetTypesChange) {
      onAssetTypesChange(portfolioId, assetId, assetTypeValues);
    }

    setSelectedAssetTypes(assetTypeValues);
  }, [portfolioId, assetId]);

  const tagsChange: Function = useCallback((tagValues: Tag[]): void => {
    if (onTagsChange) {
      onTagsChange(assetId, portfolioId, tagValues);
    }

    setSelectedTags(tagValues);
  }, [assetId, portfolioId]);

  const openNewSellerForm: Function = useCallback((): void => {
    if (onOpenNewSellerForm) {
      onOpenNewSellerForm();
    }
  }, []);

  const closeNewSellerForm: MouseEventHandlerFunction = useCallback((): void => {
    if (onCloseNewSellerForm) {
      onCloseNewSellerForm();
    }
  }, []);

  return (
    <ModalLayout
      isOpen={isOpen}
      title="New Asset"
      onCloseModal={cancelChangesAndCloseModal}
      onCloseModalByClickAway={closeModalByClickAway}
    >
      <Form>
        <Card isContainer withNoPadding>
          <Table>
            <TableBody isPlain withNoBorders withTwoColumns withHeaderColumn>
              <TableRow>
                <TableCell>
                  Photo
                </TableCell>
                <TableCell>
                  <ImageFileContainer withNoOffset>
                    <FormFieldUpload
                      isImage
                      isDroppable
                      isSmallImage
                      name={`${formPrefix}.mainPhoto`}
                      uploadFile={assetPhotoFile}
                      buttonLabel="New Asset"
                      acceptedExtensionList={FILE_EXTENSION_LIST_IMAGE}
                      onFileChange={onAssetPhotoFileChange}
                      onFileDelete={onAssetPhotoFileDelete}
                    />
                  </ImageFileContainer>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Portfolio
                  <RequiredMark />
                </TableCell>
                <TableCell>
                  <FormFieldSelect
                    isBordered
                    withBlankElement
                    isDisabled={portfolioList && !portfolioList.length}
                    name={`${formPrefix}.portfolioId`}
                    optionsTotal={portfolioList && portfolioList.length}
                  >
                    {menuItemPortfolioList}
                  </FormFieldSelect>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Name
                  <RequiredMark />
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.name`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Status
                  <RequiredMark />
                </TableCell>
                <TableCell>
                  <FormFieldSelect
                    isBordered
                    isDisabled={assetStatusList && !assetStatusList.length}
                    name={`${formPrefix}.status`}
                    optionsTotal={assetStatusList && assetStatusList.length}
                  >
                    {menuItemAssetStatusList}
                  </FormFieldSelect>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Address
                </TableCell>
                <TableCell>
                  <FieldAddressBlock>
                    <FormFieldAddress
                      isModal
                      isBordered
                      itemId={assetId}
                      name={`${formPrefix}.streetAddress`}
                      onAddressSearchCompleted={(addressObject: JsonObject): void => {
                        onAddressSearchCompleted(addressObject, assetId, portfolioId);
                      }}
                      onKeyDown={(): void => {
                        onAddressFieldKeyDown(assetId, portfolioId);
                      }}
                    />
                    <FormFieldText isDisabled isBordered name={`${formPrefix}.location`} />
                    <FormFieldText isDisabled isBordered name={`${formPrefix}.postalCode`} />
                    <FormFieldText isHidden name={`${formPrefix}.address`} />
                  </FieldAddressBlock>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Address Details
                </TableCell>
                <TableCell>
                  <FormFieldText
                    isBordered
                    isMultiline
                    name={`${formPrefix}.addressDetails`}
                    maxRows={FormService.TEXTAREA_MAX_ROWS_SMALL}
                    minRows={FormService.TEXTAREA_MIN_ROWS_DEFAULT}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Size
                </TableCell>
                <TableCell>
                  <FieldSeparatedBlock withOffset>
                    <FormFieldNumber isBordered name={`${formPrefix}.size`} />
                    <FormFieldSelect
                      isBordered
                      withBlankElement
                      isDisabled={measureUnitList && !measureUnitList.length}
                      name={`${formPrefix}.sizeType`}
                      optionsTotal={measureUnitList && measureUnitList.length}
                    >
                      {menuItemMeasureUnitList}
                    </FormFieldSelect>
                  </FieldSeparatedBlock>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Total Capitalization
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.amount`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Total Value
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.value`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Loan Amount
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.loanAmount`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Location Property Identifier
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.parcelNumber`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Submarket
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.submarket`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Lot Size
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.lotSize`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Ownership Structure
                </TableCell>
                <TableCell>
                  <FormFieldSelect
                    isBordered
                    withBlankElement
                    isDisabled={assetOwnershipStructureTypeList && !assetOwnershipStructureTypeList.length}
                    name={`${formPrefix}.assetOwnershipStructure`}
                    optionsTotal={assetOwnershipStructureTypeList && assetOwnershipStructureTypeList.length}
                  >
                    {menuItemAssetOwnershipStructureTypeList}
                  </FormFieldSelect>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Affordable Units
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.affordableUnits`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  As-of-Right Zoning
                </TableCell>
                <TableCell>
                  <FormFieldCheckbox name={`${formPrefix}.asOfRightZoning`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Cost
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.cost`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Occupancy
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.occupancy`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Current NOI
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.currentNoi`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  TTM NOI
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.ttmNoi`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Run Rate NOI
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.runRateNoi`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Forward NOI
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.forwardNoi`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Maturity Date
                </TableCell>
                <TableCell>
                  <FormFieldDate
                    isBordered
                    withBlankValues
                    withNoUnderline
                    name={`${formPrefix}.maturityDate`}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Sellers
                </TableCell>
                <TableCell>
                  <FormFieldMultiSelect
                    isBordered
                    withImage
                    withSearch
                    isDisabled={contactCompanyList && !contactCompanyList.length}
                    name={`${formPrefix}.sellerIds`}
                    onSearch={onContactCompanySearch}
                    onClose={onContactCompanySearchClose}
                    optionList={contactCompanyList}
                    onValuesChange={sellersChange}
                    defaultImage={CompanyDefaultImage}
                    newItemComponent={(
                      !isNewSellerVisible && (
                        <MenuItemAddNewItem
                          label="New Contact"
                          createPermission={CONTACT_CREATE}
                          onClick={openNewSellerForm}
                        />
                      )
                    )}
                  >
                    {menuItemContactCompanySellerList}
                  </FormFieldMultiSelect>
                </TableCell>
              </TableRow>
              {
                isNewSellerVisible && (
                  <TableRow>
                    <TableCell>
                      Seller (new)
                    </TableCell>
                    <TableCell isTextRight>
                      <ActionButton onClick={closeNewSellerForm}>
                        <CancelFilledIcon />
                      </ActionButton>
                    </TableCell>
                  </TableRow>
                )
              }
              {
                isNewSellerVisible && (
                  <TableRow>
                    <TableCell>
                      Seller Name
                      <RequiredMark />
                    </TableCell>
                    <TableCell>
                      <FormFieldText isBordered withAutoFocus={isNewSellerVisible} name={`${formPrefix}.seller.name`} />
                    </TableCell>
                  </TableRow>
                )
              }
              {
                isNewSellerVisible && (
                  <TableRow>
                    <TableCell>
                      Seller Website
                      <Hint
                        title={'Fill in Website field to search and automatically populate other fields '
                          + 'of the company, if it is listed in our dictionary'}
                      />
                    </TableCell>
                    <TableCell>
                      <FormFieldText
                        isBordered
                        name={`${formPrefix}.seller.website`}
                      />
                    </TableCell>
                  </TableRow>
                )
              }
              <TableRow>
                <TableCell>
                  Portfolio Invested Equity
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.portfolioInvestedEquity`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Levered IRR
                </TableCell>
                <TableCell>
                  <FormFieldNumber
                    isFloat
                    isPercent
                    isBordered
                    name={`${formPrefix}.leveredIrr`}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Loan Number
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.loanNumber`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Equity Multiple
                </TableCell>
                <TableCell>
                  <FormFieldNumber isFloat isBordered name={`${formPrefix}.equityMultiple`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  LEED Designation
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.leedDesignation`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Owner Entity Name
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.ownerEntityName`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Future NOI
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.futureNoi`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Investment Entity Name
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.investmentEntityName`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Investment Amount Committed
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.investmentAmountCommitted`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Investment Amount Unfunded
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.investmentAmountUnfunded`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Investment Amount Funded
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.investmentAmountFunded`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Current Market Value
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.currentMarketValue`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Close Date
                </TableCell>
                <TableCell>
                  <FormFieldDate
                    isBordered
                    withBlankValues
                    withNoUnderline
                    name={`${formPrefix}.closeDate`}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Extensions
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.extensions`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Amount Invested
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.amountInvested`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Projected Stabilized NOI
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.projectedStabilizedNoi`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Going In Cap Rate
                </TableCell>
                <TableCell>
                  <FormFieldNumber isFloat isBordered name={`${formPrefix}.goingInCapRate`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Untrended Yield To Cost
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.untrendedYieldToCost`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Projected Unlevered IRR
                </TableCell>
                <TableCell>
                  <FormFieldNumber isFloat isBordered name={`${formPrefix}.projectedUnleveredIrr`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Projected Unlevered Equity Multiple
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.projectedUnleveredEquityMultiple`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Projected Unlevered Net Profit
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.projectedUnleveredNetProfit`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Projected Levered IRR
                </TableCell>
                <TableCell>
                  <FormFieldNumber isFloat isBordered name={`${formPrefix}.projectedLeveredIrr`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Projected Levered Equity Multiple
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.projectedLeveredEquityMultiple`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Projected Levered Net Profit
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.projectedLeveredNetProfit`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Senior Lender Name
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.seniorLenderName`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Senior Loan Amount
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.seniorLoanAmount`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Senior Loan Closing Date
                </TableCell>
                <TableCell>
                  <FormFieldDate
                    isBordered
                    withBlankValues
                    withNoUnderline
                    name={`${formPrefix}.seniorLoanClosingDate`}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Senior Loan Maturity Date
                </TableCell>
                <TableCell>
                  <FormFieldDate
                    isBordered
                    withBlankValues
                    withNoUnderline
                    name={`${formPrefix}.seniorLoanMaturityDate`}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Senior Loan Interest Rate
                </TableCell>
                <TableCell>
                  <FormFieldSelect
                    isBordered
                    withBlankElement
                    isDisabled={pricingTypeList && !pricingTypeList.length}
                    name={`${formPrefix}.seniorLoanInterestRate`}
                    optionsTotal={pricingTypeList && pricingTypeList.length}
                  >
                    {menuItemPricingTypeList}
                  </FormFieldSelect>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Senior Loan Interest Spread
                </TableCell>
                <TableCell>
                  <FormFieldNumber isFloat isBordered name={`${formPrefix}.seniorLoanInterestSpread`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Senior Loan LTV
                </TableCell>
                <TableCell>
                  <FormFieldNumber isFloat isBordered name={`${formPrefix}.seniorLoanLtv`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Senior Loan LTC
                </TableCell>
                <TableCell>
                  <FormFieldNumber isFloat isBordered name={`${formPrefix}.seniorLoanLtc`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Mezzanine Lender Name
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.mezzanineLenderName`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Mezzanine Loan Amount
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.mezzanineLoanAmount`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Mezzanine Loan Closing Date
                </TableCell>
                <TableCell>
                  <FormFieldDate
                    isBordered
                    withBlankValues
                    withNoUnderline
                    name={`${formPrefix}.mezzanineLoanClosingDate`}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Mezzanine Loan Maturity Date
                </TableCell>
                <TableCell>
                  <FormFieldDate
                    isBordered
                    withBlankValues
                    withNoUnderline
                    name={`${formPrefix}.mezzanineLoanMaturityDate`}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Mezzanine Loan Interest Rate
                </TableCell>
                <TableCell>
                  <FormFieldSelect
                    isBordered
                    withBlankElement
                    isDisabled={pricingTypeList && !pricingTypeList.length}
                    name={`${formPrefix}.mezzanineLoanInterestRate`}
                    optionsTotal={pricingTypeList && pricingTypeList.length}
                  >
                    {menuItemPricingTypeList}
                  </FormFieldSelect>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Mezzanine Loan Interest Spread
                </TableCell>
                <TableCell>
                  <FormFieldNumber isFloat isBordered name={`${formPrefix}.mezzanineLoanInterestSpread`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Mezzanine Loan LTV
                </TableCell>
                <TableCell>
                  <FormFieldNumber isFloat isBordered name={`${formPrefix}.mezzanineLoanLtv`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Mezzanine Loan LTC
                </TableCell>
                <TableCell>
                  <FormFieldNumber isFloat isBordered name={`${formPrefix}.mezzanineLoanLtc`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Blended Loan Interest Rate
                </TableCell>
                <TableCell>
                  <FormFieldSelect
                    isBordered
                    withBlankElement
                    isDisabled={pricingTypeList && !pricingTypeList.length}
                    name={`${formPrefix}.blendedLoanInterestRate`}
                    optionsTotal={pricingTypeList && pricingTypeList.length}
                  >
                    {menuItemPricingTypeList}
                  </FormFieldSelect>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Blended Loan Interest Spread
                </TableCell>
                <TableCell>
                  <FormFieldNumber isFloat isBordered name={`${formPrefix}.blendedLoanInterestSpread`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Note Principal Balance at Origination
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.notePrincipalBalanceAtOrigination`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Accrued Interest
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.accruedInterest`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Market Type
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.marketType`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Year Built
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered withNoCommas name={`${formPrefix}.yearBuilt`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Year Renovated
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered withNoCommas name={`${formPrefix}.yearRenovated`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  First Payment Date
                </TableCell>
                <TableCell>
                  <FormFieldDate
                    isBordered
                    withBlankValues
                    withNoUnderline
                    name={`${formPrefix}.firstPaymentDate`}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Next Payment Due Date
                </TableCell>
                <TableCell>
                  <FormFieldDate
                    isBordered
                    withBlankValues
                    withNoUnderline
                    name={`${formPrefix}.nextPaymentDueDate`}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Original Loan Term Month
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered withNoCommas name={`${formPrefix}.originalLoanTermMonth`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Loan Age
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered withNoCommas name={`${formPrefix}.loanAge`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Remaining Term
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered withNoCommas name={`${formPrefix}.remainingTerm`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Amortization Period Months
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered withNoCommas name={`${formPrefix}.amortizationPeriodMonths`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Remaining Amortization Period
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered withNoCommas name={`${formPrefix}.remainingAmortizationPeriod`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Payment String Year
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.paymentStringYear`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Amortization Type
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.amortizationType`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Interest Only Period
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered withNoCommas name={`${formPrefix}.interestOnlyPeriod`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Remaining Interest Only Period
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered withNoCommas name={`${formPrefix}.remainingInterestOnlyPeriod`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Interest Accrual Method
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.interestAccrualMethod`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Primary Loan Purpose
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.primaryLoanPurpose`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Underlying Loan Type
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.underlyingLoanType`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Lien Position
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered withNoCommas name={`${formPrefix}.lienPosition`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Primary Collateral
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.primaryCollateral`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Security Interest
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.securityInterest`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Ground Lease
                </TableCell>
                <TableCell>
                  <FormFieldCheckbox name={`${formPrefix}.groundLease`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Prepayment Provisions
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.prepaymentProvisions`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Remaining Prepayment Provisions
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.remainingPrepaymentProvisions`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Current Credit Letter Amount
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.currentCreditLetterAmount`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Current Credit Letter Credit Expiration Date
                </TableCell>
                <TableCell>
                  <FormFieldDate
                    isBordered
                    withBlankValues
                    withNoUnderline
                    name={`${formPrefix}.currentCreditLetterCreditExpirationDate`}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Interest Reserves at Origination
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.interestReservesAtOrigination`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Tax Insurance Escrows at Origination
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.taxInsuranceEscrowsAtOrigination`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Cap Ex Reserves at Origination
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.capitalExpenditureReservesAtOrigination`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Other Escrow and Reserves at Origination
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.otherEscrowAndReservesAtOrigination`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Current Interest Reserves
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.currentInterestReserves`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Current Tax Insurance Escrows
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.currentTaxInsuranceEscrows`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Current Cap Ex Escrows
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.currentCapitalExpenditureEscrows`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Current Other Escrows and Reserves
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.currentOtherEscrowsAndReserves`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Initial Appraisal Date
                </TableCell>
                <TableCell>
                  <FormFieldDate
                    isBordered
                    withBlankValues
                    withNoUnderline
                    name={`${formPrefix}.initialAppraisalDate`}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Stabilized Appraised NOI
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.stabilizedAppraisedNoi`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Stabilized Occupancy
                </TableCell>
                <TableCell>
                  <FormFieldNumber isFloat isBordered name={`${formPrefix}.stabilizedOccupancy`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Stabilized Capitalization Rate
                </TableCell>
                <TableCell>
                  <FormFieldNumber isFloat isBordered name={`${formPrefix}.stabilizedCapitalizationRate`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Most Recent Appraisal Date
                </TableCell>
                <TableCell>
                  <FormFieldDate
                    isBordered
                    withBlankValues
                    withNoUnderline
                    name={`${formPrefix}.mostRecentAppraisalDate`}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  At Close Revenue
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.atCloseRevenue`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  At Close Expenses
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.atCloseExpenses`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  At Close NOI
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered name={`${formPrefix}.atCloseNoi`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  At Close Debt Yield NOI
                </TableCell>
                <TableCell>
                  <FormFieldNumber isFloat isBordered name={`${formPrefix}.atCloseDebtYieldNoi`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  At Close DSCR NOI
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.atCloseDscrNoi`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  At Close NCF
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.atCloseNcf`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  At Close Debt Yield NCF
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.atCloseDebtYieldNcf`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  At Close DSCR NCF
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.atCloseDscrNcf`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Occupancy at Origination
                </TableCell>
                <TableCell>
                  <FormFieldNumber isFloat isBordered name={`${formPrefix}.occupancyAtOrigination`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  At Close NOI and Occupancy Date
                </TableCell>
                <TableCell>
                  <FormFieldDate
                    isBordered
                    withBlankValues
                    withNoUnderline
                    name={`${formPrefix}.atCloseNoiAndOccupancyDate`}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Stabilized NOI at Origination
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.stabilizedNoiAtOrigination`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Stabilized NCF at Origination
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.stabilizedNcfAtOrigination`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  1st Extended Maturity Date
                </TableCell>
                <TableCell>
                  <FormFieldDate
                    isBordered
                    withBlankValues
                    withNoUnderline
                    name={`${formPrefix}.firstExtendedMaturityDate`}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  2nd Extended Maturity Date
                </TableCell>
                <TableCell>
                  <FormFieldDate
                    isBordered
                    withBlankValues
                    withNoUnderline
                    name={`${formPrefix}.secondExtendedMaturityDate`}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  3rd Extended Maturity Date
                </TableCell>
                <TableCell>
                  <FormFieldDate
                    isBordered
                    withBlankValues
                    withNoUnderline
                    name={`${formPrefix}.thirdExtendedMaturityDate`}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Yardi Asset ID
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.yardiAssetId`} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Asset Classes
                </TableCell>
                <TableCell>
                  <FormFieldMultiSelect
                    isBordered
                    isDisabled={assetTypeList && !assetTypeList.length}
                    keyField="name"
                    name={`${formPrefix}.types`}
                    optionList={assetTypeList}
                    onValuesChange={assetTypesChange}
                  >
                    {menuItemAssetTypeList}
                  </FormFieldMultiSelect>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Tags
                </TableCell>
                <TableCell>
                  <FormFieldMultiSelect
                    isBordered
                    isAbleToAdd
                    withSearch
                    isDisabled={tagList && !tagList.length}
                    keyField="name"
                    name={`${formPrefix}.tags`}
                    optionList={tagList}
                    onSearch={onTagSearch}
                    onClose={onTagSearchClose}
                    onValuesChange={tagsChange}
                  >
                    {menuItemTagList}
                  </FormFieldMultiSelect>
                </TableCell>
              </TableRow>
              {
                (assetFieldNewItems || []).map((assetFieldItem: AssetFieldInput): ReactElement => {
                  const formKeyAssetField: string = FormService.getIndexedFormFieldKey(
                    assetFieldItem.id as number,
                  );

                  return (
                    <TableRow key={assetFieldItem.id}>
                      <TableCell>
                        <FormFieldText
                          isHidden
                          name={`${formPrefix}.options[${formKeyAssetField}].id`}
                        />
                        <FormFieldText
                          isBordered
                          name={`${formPrefix}.options[${formKeyAssetField}].name`}
                          placeholder="Name"
                        />
                      </TableCell>
                      <TableCell>
                        <FlexContainer>
                          <FormFieldText
                            isBordered
                            name={`${formPrefix}.options[${formKeyAssetField}].value`}
                            placeholder="Value"
                          />
                          <Tooltip title="Delete">
                            <ActionButton
                              isInline
                              withOffset
                              withSmallPadding
                              onClick={(): void => {
                                deleteClick(assetFieldItem.id);
                              }}
                            >
                              <RemoveIcon />
                            </ActionButton>
                          </Tooltip>
                        </FlexContainer>
                      </TableCell>
                    </TableRow>
                  );
                })
              }
              <TableRowAddNewItem colSpan={COLUMNS_TOTAL + 1} onClick={addNewItem} />
            </TableBody>
          </Table>
        </Card>
        <ModalButtonBlock isFixed>
          <Button onClick={cancelChangesAndCloseModal}>
            Cancel
          </Button>
          <Button isActive isDisabled={isSaving} onClick={applyChangesAndCloseModal}>
            Save
          </Button>
          {
            isSaving && !isSmallScreen && (
              <Loader isInline isModal isFloating isSmall />
            )
          }
        </ModalButtonBlock>
      </Form>
      <LeaveConfirmModal
        isSubModal
        isOpen={isLeaveConfirmModalOpen}
        onConfirm={onLeaveConfirm}
        onCloseModal={closeLeaveConfirmModal}
      />
    </ModalLayout>
  );
});

export default AssetModal;
